import { useState, useEffect } from 'react';
import { api } from '../api';

export default function BudgetTracker({ refreshKey }) {
  const [budget, setBudget] = useState(null);

  useEffect(() => {
    api.getBudgetSummary().then(setBudget).catch(() => {});
  }, [refreshKey]);

  if (!budget) return null;

  const amexPct = budget.amex_total ? (budget.amex_used / budget.amex_total) * 100 : 0;
  const chasePct = budget.chase_total ? (budget.chase_used / budget.chase_total) * 100 : 0;

  const flightSel = budget.selections?.find(s => s.selection_type === 'flight');
  const hotelSel = budget.selections?.find(s => s.selection_type === 'hotel');
  const activitySels = budget.selections?.filter(s => s.selection_type === 'activity') || [];
  const restaurantSels = budget.selections?.filter(s => s.selection_type === 'restaurant') || [];

  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-4 space-y-4">
      <div className="text-xs font-bold text-brand-ink/70 uppercase tracking-wider">Live Budget Tracker</div>

      {/* Points Bars */}
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-blue-300 font-medium">Amex MR</span>
            <span className="text-brand-ink/75">{budget.amex_remaining?.toLocaleString()} left</span>
          </div>
          <div className="h-3 bg-brand-ink/10 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${amexPct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-brand-ink/55 mt-0.5">
            <span>{budget.amex_used?.toLocaleString()} used</span>
            <span>{budget.amex_total?.toLocaleString()} total</span>
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-indigo-300 font-medium">Chase UR</span>
            <span className="text-brand-ink/75">{budget.chase_remaining?.toLocaleString()} left</span>
          </div>
          <div className="h-3 bg-brand-ink/10 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-all duration-700" style={{ width: `${chasePct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-brand-ink/55 mt-0.5">
            <span>{budget.chase_used?.toLocaleString()} used</span>
            <span>{budget.chase_total?.toLocaleString()} total</span>
          </div>
        </div>
      </div>

      {/* Cash Total */}
      <div className="bg-brand-ink/5 rounded-lg p-3">
        <div className="flex justify-between items-center">
          <span className="text-xs text-brand-ink/70">Cash out of pocket</span>
          <span className="text-lg font-bold text-gold">${budget.cash_total?.toLocaleString()}</span>
        </div>
      </div>

      {/* Selections Summary */}
      <div className="space-y-2">
        <div className="text-[10px] text-brand-ink/65 uppercase tracking-wider">YOUR PICKS</div>

        <SelectionRow
          label="Flight"
          selected={!!flightSel}
          detail={flightSel?.notes}
          points={flightSel?.points_cost}
          cash={flightSel?.cash_cost}
          source={flightSel?.source_program}
        />
        <SelectionRow
          label="Hotel"
          selected={!!hotelSel}
          detail={hotelSel?.notes}
          points={hotelSel?.points_cost}
          cash={hotelSel?.cash_cost}
          source={hotelSel?.source_program}
        />
        <SelectionRow
          label={`Activities (${activitySels.length})`}
          selected={activitySels.length > 0}
          cash={activitySels.reduce((s, a) => s + (a.cash_cost || 0), 0)}
        />
        <SelectionRow
          label={`Restaurants (${restaurantSels.length})`}
          selected={restaurantSels.length > 0}
          cash={restaurantSels.reduce((s, r) => s + (r.cash_cost || 0), 0)}
        />
      </div>

      {/* Total */}
      <div className="border-t border-brand-ink/10 pt-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-brand-ink/75">Total Points Used</span>
          <span className="text-sm font-bold">{(budget.amex_used + budget.chase_used).toLocaleString()}</span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-sm font-medium text-brand-ink/75">Total Cash</span>
          <span className="text-sm font-bold text-gold">${budget.cash_total?.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function SelectionRow({ label, selected, detail, points, cash, source }) {
  return (
    <div className={`flex items-center justify-between py-1.5 px-2 rounded ${selected ? 'bg-teal/5' : 'bg-white/[0.02]'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${selected ? 'bg-teal' : 'bg-brand-ink/20'}`} />
        <span className={`text-xs ${selected ? 'text-brand-ink/85' : 'text-brand-ink/55'}`}>{label}</span>
      </div>
      <div className="text-right">
        {points > 0 && <div className="text-[10px] text-brand-ink/70">{points.toLocaleString()} pts</div>}
        {cash > 0 && <div className="text-[10px] text-gold">${cash.toLocaleString()}</div>}
        {!selected && <div className="text-[10px] text-brand-ink/45">Not selected</div>}
      </div>
    </div>
  );
}
